import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint } from '../../hooks/useBrowser';
import { bridge } from '../../lib/desktop';
import { Dock } from './Dock';
import { TopBar } from './TopBar';
import { MobileNav } from './MobileNav';
import { RingAlert } from './RingAlert';
import { CommandPaletteProvider } from './CommandPalette';

/**
 * App frame: icon dock on the left, translucent top bar, and the content on a canvas where each
 * page lays out its own floating panels. On phones the dock becomes a bottom tab bar.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const bp = useBreakpoint();
  const navigate = useNavigate();

  // desktop notification/alert window asks us to open a thread
  useEffect(() => {
    const b = bridge();
    if (!b) return;
    try {
      return b.onOpenThread((id) => navigate(`/inbox/${id}`));
    } catch {
      return undefined;
    }
  }, [navigate]);

  return (
    <CommandPaletteProvider>
      {/* `relative` makes the shell the containing block for absolutely positioned descendants (e.g. sr-only
          labels deep inside scrolled panes), so they're clipped here instead of stretching and scrolling the page. */}
      <div className="mp-canvas relative flex h-screen h-[100dvh] overflow-hidden">
        <a href="#main" className="sr-only-focusable fixed left-2 top-2 z-[200] rounded-xl bg-primary px-3 py-2 text-white">
          Skip to content
        </a>
        {bp !== 'mobile' && <Dock />}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar />
          <main id="main" className="min-h-0 min-w-0 flex-1" style={bp === 'mobile' ? { paddingBottom: 'calc(72px + env(safe-area-inset-bottom))' } : undefined}>
            {children}
          </main>
        </div>
        {bp === 'mobile' && <MobileNav />}
        <RingAlert />
      </div>
    </CommandPaletteProvider>
  );
}
